import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import './resultEntryFormula.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppDialog } from '../../components/AppDialog';
import { AppToolbar } from '../../components/AppToolbar';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { FormTitle } from '../../components/FormTitle';
import { ViewToggle } from '../../components/ViewToggle';
import { useConfirm, useSaveFlow, useDeleteFlow } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { useNavPermission } from '../../hooks/useNavPermission';
import { useModalKeys } from '../../hooks/useModalKeys';

/**
 * Settings ▸ SDMS ▸ Result Entry Formula — the Result Entry calculation
 * ASSIGNMENT (dbo.ResultEntry_Formula, legacy/shared table), keyed by the EXACT Test + Matrix +
 * Method combination (owner decision 2026-07-28: every Result Entry row has
 * exactly one TestMethod, so no blank/fallback matching). Mirrors the legacy
 * ALPACA "Template Formula" screen shape (mapped combos + "Add Formulas"), but
 * STANDALONE — the shared "Formula Library" (dbo.SpreadSheetBuilder_Formula,
 * used by BOTH SDMS templates and this form) is its OWN full-CRUD form via the
 * generic Settings grid, not a tab here.
 *
 * Follows the SAME structure/action-bar position/validation/audit pattern as
 * every other Settings form (§18.6/§18.8): shared FormTitle (title + load-time
 * + audit chip), shared AppToolbar (canonical New/Edit/Delete/Refresh/Export
 * order), shared useSaveFlow/useDeleteFlow ("Do you want to Save?" → save →
 * "Saved successfully." / danger-confirm → "Deleted."), server-authoritative
 * validation + 403 on missing Write, and audit via IAuditTrailService
 * (Create/Update/Delete). Exceptions are captured automatically by the global
 * ExceptionHandlingMiddleware into dbo.AI_ExceptionLog — no per-form code needed.
 */

interface MapRow {
  testMethodOid: string; test: string | null; matrix: string | null; method: string | null;
  formulaCount: number; isActive: string; modifiedAt: string | null; modifiedBy: string | null;
}
interface LibRow { formulaUqId: number; formulaID: string | null; formulaName: string; kind: number; targetField: string | null; series: number }
interface Combo  { testMethodOid: string; test: string | null; matrix: string | null; method: string | null }
interface DetailRow { formulaUqId: number; executionOrder: number; formulaName?: string; targetField?: string | null }

// Standard read-only detail control (owner request 2026-10-15 "correct the detail view
// control to standard"): 26px tall, the grey read-only cue (#f6f7f9) + app ink/border,
// full-width in its grid column — matching the shared SettingsDetailPage read-only field.
const REF_RO_INPUT: CSSProperties = {
  height: 26, fontSize: 13, padding: '0 8px', border: '1px solid var(--ai-line)',
  borderRadius: 4, background: '#f6f7f9', color: 'var(--ai-ink)',
  width: '100%', boxSizing: 'border-box',
};

export default function ResultEntryFormulaPage({ onHome }: { onHome?: () => void }) {
  const { alert } = useConfirm();
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const perm = useNavPermission('Result Entry');

  const [maps, setMaps] = useState<MapRow[]>([]);
  const [lib, setLib] = useState<LibRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [editor, setEditor] = useState<{ combo: Combo | null; picked: Map<number, number>; viewOnly: boolean } | null>(null);
  // Search box in the Edit/Add Formulas popup (owner request 2026-10-15: replace the
  // test-title header with a search to filter the ~15 library formulas). Reset per test.
  const [editorSearch, setEditorSearch] = useState('');
  useEffect(() => { setEditorSearch(''); }, [editor?.combo?.testMethodOid]);
  // Formulas shown in the popup: all library formulas when editing, the picked ones
  // (in order) when viewing — then narrowed by the search box.
  const editorFormulas = useMemo(() => {
    if (!editor) return [] as LibRow[];
    const base = editor.viewOnly
      ? lib.filter(f => editor.picked.has(f.formulaUqId))
          .sort((a, b) => (editor.picked.get(a.formulaUqId) ?? 0) - (editor.picked.get(b.formulaUqId) ?? 0))
      : lib;
    const q = editorSearch.trim().toLowerCase();
    return q ? base.filter(f => (f.formulaName ?? '').toLowerCase().includes(q)) : base;
  }, [editor, lib, editorSearch]);
  const [combos, setCombos] = useState<Combo[]>([]);
  const [comboSearch, setComboSearch] = useState('');
  // List / Detail view toggle (standing rule 10 / §18.6 parity with every other form).
  // The Detail View is a full-page READ-ONLY view of one mapping's assigned formulas
  // (the eye "View" column opens it); Add / Edit still use the popup.
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [detail, setDetail] = useState<{ row: MapRow; picked: Map<number, number> } | null>(null);
  // Maximize (Zoom) — page-level flex fill (NOT the grid's absolute overlay), so the
  // search row + Export/Restore stay visible when zoomed, matching the ModuleListView
  // list forms (e.g. Document Creator). Esc restores.
  const [fullGrid, setFullGrid] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const [m, l] = await Promise.all([
        api.get('/api/v1/result-entry-formula/mappings', { silentLoading: true } as never),
        api.get('/api/v1/result-entry-formula/library', { silentLoading: true } as never),
      ]);
      setMaps((m.data as { data: MapRow[] }).data ?? []);
      setLib((l.data as { data: LibRow[] }).data ?? []);
    } catch {
      notify.error('Failed to load Result Entry Formula mappings.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  // Esc restores the maximized grid.
  useEffect(() => {
    if (!fullGrid) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullGrid(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullGrid]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return maps;
    return maps.filter(m => [m.test, m.matrix, m.method].some(v => (v ?? '').toLowerCase().includes(q)));
  }, [maps, search]);

  const selectedRow = maps.find(m => selected.has(m.testMethodOid));

  const openEditor = useCallback(async (existing?: MapRow, viewOnly = false) => {
    try {
      if (!viewOnly && combos.length === 0) {
        const r = await api.get('/api/v1/result-entry-formula/test-methods');
        setCombos((r.data as { data: Combo[] }).data ?? []);
      }
      const picked = new Map<number, number>();
      let combo: Combo | null = null;
      if (existing) {
        combo = { testMethodOid: existing.testMethodOid, test: existing.test, matrix: existing.matrix, method: existing.method };
        const d = await api.get(`/api/v1/result-entry-formula/mappings/${existing.testMethodOid}`);
        for (const row of ((d.data as { data: DetailRow[] }).data ?? [])) picked.set(row.formulaUqId, row.executionOrder);
      }
      setComboSearch('');
      setEditor({ combo, picked, viewOnly });
    } catch {
      notify.error('Could not open the formula editor.');
    }
  }, [combos.length, notify]);

  // Open a mapping in the full-page Detail View (read-only) — the eye "View" column
  // and the Detail View tab both use this. Loads the row's assigned formulas.
  const openDetail = useCallback(async (row: MapRow) => {
    try {
      const d = await api.get(`/api/v1/result-entry-formula/mappings/${row.testMethodOid}`);
      const picked = new Map<number, number>();
      for (const r of ((d.data as { data: DetailRow[] }).data ?? [])) picked.set(r.formulaUqId, r.executionOrder);
      setDetail({ row, picked });
      setView('detail');
    } catch {
      notify.error('Could not open the mapping detail.');
    }
  }, [notify]);

  const saveEditor = useCallback(async () => {
    if (!editor?.combo) { void alert({ title: 'Formula Setup', message: 'Select a Test / Matrix / Method first.', tone: 'warning' }); return; }
    if (editor.picked.size === 0) { void alert({ title: 'Formula Setup', message: 'Pick at least one formula.', tone: 'warning' }); return; }
    const isNew = !maps.some(m => m.testMethodOid === editor.combo!.testMethodOid);
    const formulas = [...editor.picked.entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([formulaUqId, executionOrder]) => ({ formulaUqId, executionOrder }));

    await runSave({
      formName: 'Result Entry Formula',
      auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        await api.put(`/api/v1/result-entry-formula/mappings/${editor.combo!.testMethodOid}`, {
          testMethodOid: editor.combo!.testMethodOid,
          test: editor.combo!.test ?? '', matrix: editor.combo!.matrix ?? '', method: editor.combo!.method ?? '',
          formulas,
        });
      },
      onSaved: () => { setEditor(null); void load(); },
    });
  }, [editor, maps, runSave, load, alert]);

  // Remove Formula — a popup lists the selected test method's assigned formulas; the
  // user ticks the ones to remove (owner request — remove specific formulas, not all).
  // On confirm, the remaining set is PUT back (or the mapping deleted if none remain).
  const [removeState, setRemoveState] = useState<{ row: MapRow; formulas: DetailRow[]; toRemove: Set<number> } | null>(null);
  const openRemove = useCallback(async () => {
    if (!selectedRow) { void alert({ title: 'Remove Formula', message: 'Select a test method first.', tone: 'warning' }); return; }
    if (selectedRow.formulaCount === 0) { void alert({ title: 'Remove Formula', message: 'This test method has no formulas to remove.', tone: 'warning' }); return; }
    try {
      const d = await api.get(`/api/v1/result-entry-formula/mappings/${selectedRow.testMethodOid}`);
      const formulas = ((d.data as { data: DetailRow[] }).data ?? []);
      setRemoveState({ row: selectedRow, formulas, toRemove: new Set() });
    } catch { notify.error('Could not load the assigned formulas.'); }
  }, [selectedRow, alert, notify]);

  const confirmRemove = useCallback(async () => {
    if (!removeState) return;
    const { row, formulas, toRemove } = removeState;
    if (toRemove.size === 0) { void alert({ title: 'Remove Formula', message: 'Select at least one formula to remove.', tone: 'warning' }); return; }
    const remaining = formulas.filter(f => !toRemove.has(f.formulaUqId))
      .map(f => ({ formulaUqId: f.formulaUqId, executionOrder: f.executionOrder }));
    setRemoveState(null);
    await runDelete({
      formName: 'Result Entry Formula',
      message: `Remove ${toRemove.size} formula${toRemove.size === 1 ? '' : 's'} from ${row.test}?`,
      delete: async () => {
        if (remaining.length === 0) {
          await api.delete(`/api/v1/result-entry-formula/mappings/${row.testMethodOid}`);
        } else {
          await api.put(`/api/v1/result-entry-formula/mappings/${row.testMethodOid}`, {
            testMethodOid: row.testMethodOid, test: row.test ?? '', matrix: row.matrix ?? '', method: row.method ?? '', formulas: remaining,
          });
        }
      },
      onDeleted: () => { setSelected(new Set()); void load(); },
    });
  }, [removeState, runDelete, load, alert]);

  // Delete — remove ALL formulas from the selected test method in one go (legacy
  // ResultEntryFormulasViewController.MasterListDeleteAction_Executing: "Deletes all
  // formulas for the selected TestMethod and refreshes the master list"; it cancels the
  // default XAF delete so the TestMethod itself is never deleted). Distinct from Remove
  // Formula, which picks specific formulas. Reuses the existing bulk-delete endpoint.
  const deleteAll = useCallback(async () => {
    if (!selectedRow) { void alert({ title: 'Delete', message: 'Select a test method first.', tone: 'warning' }); return; }
    if (selectedRow.formulaCount === 0) { void alert({ title: 'Delete', message: 'No formulas found for the selected test method.', tone: 'warning' }); return; }
    const row = selectedRow;
    await runDelete({
      formName: 'Result Entry Formula',
      message: `Delete all ${row.formulaCount} formula${row.formulaCount === 1 ? '' : 's'} from ${row.test}?`,
      delete: async () => { await api.delete(`/api/v1/result-entry-formula/mappings/${row.testMethodOid}`); },
      onDeleted: () => { setSelected(new Set()); void load(); },
    });
  }, [selectedRow, runDelete, load, alert]);

  // Copy Formula — copy every formula from a chosen SOURCE test method onto the
  // selected target (legacy "Copy Formula", image 231). Sources are the test methods
  // that already have formulas; reuses the existing detail + save endpoints.
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySearch, setCopySearch] = useState('');
  // Single-select the SOURCE test method in the Copy Formula grid; reset on open.
  const [copySel, setCopySel] = useState<Set<string | number>>(new Set());
  useEffect(() => { if (copyOpen) { setCopySel(new Set()); setCopySearch(''); } }, [copyOpen]);
  const copyFrom = useCallback(async (source: MapRow) => {
    if (!selectedRow) return;
    const target = selectedRow;
    try {
      const d = await api.get(`/api/v1/result-entry-formula/mappings/${source.testMethodOid}`);
      const formulas = ((d.data as { data: DetailRow[] }).data ?? [])
        .map(r => ({ formulaUqId: r.formulaUqId, executionOrder: r.executionOrder }));
      if (formulas.length === 0) { void alert({ title: 'Copy Formula', message: 'That test method has no formulas to copy.', tone: 'warning' }); return; }
      setCopyOpen(false);
      await runSave({
        formName: 'Result Entry Formula',
        auditAction: target.formulaCount > 0 ? 'edit' : 'create',
        save: async () => {
          await api.put(`/api/v1/result-entry-formula/mappings/${target.testMethodOid}`, {
            testMethodOid: target.testMethodOid, test: target.test ?? '', matrix: target.matrix ?? '', method: target.method ?? '', formulas,
          });
        },
        onSaved: () => { setSelected(new Set()); void load(); },
      });
    } catch { notify.error('Could not copy formulas.'); }
  }, [selectedRow, runSave, load, alert, notify]);

  const mapCols: AppColumn<MapRow>[] = [
    { field: 'test',         header: 'Test',         width: 260, text: r => r.test ?? '' },
    { field: 'matrix',       header: 'Matrix',       width: 170, text: r => r.matrix ?? '' },
    { field: 'method',       header: 'Method',       width: 220, text: r => r.method ?? '' },
    { field: 'formulaCount', header: 'FormulaCount', width: 150, text: r => String(r.formulaCount) },
    {
      field: 'isActive', header: 'IsActive', width: 120, text: r => r.isActive,
      render: r => <input type="checkbox" checked={r.isActive === 'Yes'} disabled readOnly aria-label="Is Active" />,
    },
  ];

  const comboMatches = useMemo(() => {
    const q = comboSearch.trim().toLowerCase();
    if (!q) return combos.slice(0, 50);
    return combos.filter(c =>
      (c.test ?? '').toLowerCase().includes(q) ||
      (c.matrix ?? '').toLowerCase().includes(q) ||
      (c.method ?? '').toLowerCase().includes(q)).slice(0, 50);
  }, [combos, comboSearch]);

  // Bespoke popup keyboard: Esc closes (standing rule 14).
  const modalKeys = useModalKeys(!!editor, { onClose: () => setEditor(null) });

  return (
    <div className={`bl-page${fullGrid ? ' re-fullgrid' : ''}`}>
      <div className="bl-top">
        {/* Navigator breadcrumb (owner request 2026-10-14) — re-added WITH the subgroup:
            Home › Settings › Default Settings › Result Entry Formula. FormTitle rides the same
            row (empty in list view where it publishes the load-time + Audit chips to the toolbar;
            null in detail view) so it adds no empty strip. */}
        <div className="dp-breadcrumb" style={{ padding: '6px 0 2px' }}>
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          <span>Default Settings</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Result Entry Formula</span>
          <FormTitle pageId="reformula" title="Result Entry Formula"
            auditFormName={view === 'list' ? 'Result Entry Formula' : undefined}
            loadMs={view === 'list' ? loadMs : null}
            loading={view === 'list' ? loading : false} />
        </div>
      </div>

      <div className="bl-grid-area">
        {/* List / Detail view toggle — parity with every other form (standing rule 10).
            Detail View shows the selected/opened mapping's formulas read-only.
            Disabled unless a row is CURRENTLY selected — so at opening time (and after
            deselecting) the Detail View tab is not clickable (owner report: it was
            active at open because it keyed off a stale `detail`). */}
        <div className="re-max-hide" style={{ margin: '0 0 8px' }}>
          <ViewToggle
            view={view}
            detailDisabled={view !== 'detail' && !selectedRow}
            onChange={v => {
              if (v === 'list') { setView('list'); return; }
              if (selectedRow) void openDetail(selectedRow);
            }}
          />
        </div>

        {view === 'list' ? (
          <>
            <div className="re-max-hide" style={{ margin: '0 0 8px' }}>
              <AppToolbar
                left={
                  <>
                    {/* Delete — remove ALL formulas from the selected test method; placed
                        BEFORE Refresh (owner request 2026-10-15), legacy parity. */}
                    {perm.canWrite && (
                      <button className="ca-btn" title="Delete all formulas from the selected test method"
                        onClick={() => void deleteAll()}>
                        <span className="tb-i tb-i-red">🗑</span> Delete
                      </button>
                    )}
                    {/* Refresh icon matches the shared AppToolbar standard (🔄) used on every
                        other form (owner report 2026-10-15 — the ↻ glyph here was the outlier). */}
                    <button className="ca-btn" title="Refresh" onClick={() => void load()} disabled={loading}>
                      <span className="tb-i tb-i-green">🔄</span> Refresh
                    </button>
                    {perm.canWrite && (
                      <>
                        <button className="ca-btn" title="Add formulas to the selected test method"
                          onClick={() => {
                            if (!selectedRow) { void alert({ title: 'Add Formula', message: 'Select a test method first.', tone: 'warning' }); return; }
                            void openEditor(selectedRow);
                          }}>
                          <span className="tb-i">＋</span> Add Formula
                        </button>
                        {/* Order: Add · Remove · Copy (owner request 2026-10-12). */}
                        <button className="ca-btn" title="Remove specific formulas from the selected test method"
                          onClick={() => void openRemove()}>
                          <span className="tb-i tb-i-red">🗑</span> Remove Formula
                        </button>
                        <button className="ca-btn" title="Copy formulas from another test method"
                          onClick={() => {
                            if (!selectedRow) { void alert({ title: 'Copy Formula', message: 'Select the test method to copy formulas TO.', tone: 'warning' }); return; }
                            setCopySearch(''); setCopyOpen(true);
                          }}>
                          <span className="tb-i">⧉</span> Copy Formula
                        </button>
                      </>
                    )}
                  </>
                }
                exportData={{ filename: 'result-entry-formula', ...gridExportMatrix(filtered, mapCols) }}
              />
            </div>

            {/* Search + Export + Maximize on ONE row OUTSIDE the grid card (owner request,
                matching Document Creator's list-search-row). The grid's own header tools
                are hidden; Export + Maximize live here. Maximize is a page-level flex fill
                (re-fullgrid on .bl-page), so this row STAYS visible when zoomed. In zoom mode
                it is the top strip, so nudge it down 4px and trim 3px below (base is 0/10)
                so it is not jammed under the top bar (owner request 2026-10-14). */}
            <div className="list-search-row" style={fullGrid ? { padding: '4px 0 7px' } : undefined}>
              <div className="list-search">
                <span className="tb-i">🔍</span>
                <input type="text" placeholder="Search test, matrix, method…"
                  value={search} onChange={e => setSearch(e.target.value)} />
                {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
              </div>
              <div className="ref-grid-acts">
                <ExportMenu iconOnly triggerClassName="adg-max-btn"
                  filename="result-entry-formula" {...gridExportMatrix(filtered, mapCols)} />
                <button type="button" className="adg-max-btn" onClick={() => setFullGrid(v => !v)}
                  title={fullGrid ? 'Restore (Esc)' : 'Maximize'}
                  aria-label={fullGrid ? 'Restore' : 'Maximize'}>
                  <i className={`ti ti-arrows-${fullGrid ? 'minimize' : 'maximize'}`} />
                </button>
              </div>
            </div>

            <div className="bl-card bl-card-flex">
              <AppDataGrid<MapRow>
                rows={filtered} columns={mapCols} getRowId={r => r.testMethodOid}
                selectable selectedIds={selected} onSelectionChange={setSelected}
                onRowDoubleClick={r => void openDetail(r)}
                loading={loading} loadMs={loadMs}
                hideHeaderTools
                emptyText="No test methods found."
              />
            </div>
          </>
        ) : (
          // ── Detail View — full-page read-only view of one mapping's formulas ──
          <div className="bl-card bl-card-flex" style={{ padding: '16px 22px', overflowY: 'auto' }}>
            {detail ? (
              <>
                {/* Back to List / Edit removed (owner request) — the List View toggle tab
                    returns to the list and editing is via the list's Add Formula. A
                    "General Information" section label takes their place. */}
                <div className="ref-section-title">General Information</div>
                {/* Header fields — legacy ResultEntry_Formula detail (image 232). Read-only. */}
                <div style={{ display: 'grid', gridTemplateColumns: '150px 320px', rowGap: 6, alignItems: 'center', maxWidth: 520, marginBottom: 6 }}>
                  <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)' }}>Test Method:</label>
                  <input className="rif-in" style={REF_RO_INPUT} value={detail.row.test ?? ''} disabled readOnly />
                  <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)' }}>Method Number:<span style={{ color: 'var(--ai-red)', marginLeft: 2 }}>*</span></label>
                  <input className="rif-in" style={REF_RO_INPUT} value={detail.row.method ?? ''} disabled readOnly />
                  <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)' }}>Matrix Name:<span style={{ color: 'var(--ai-red)', marginLeft: 2 }}>*</span></label>
                  <input className="rif-in" style={REF_RO_INPUT} value={detail.row.matrix ?? ''} disabled readOnly />
                  <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)' }}>Is Active:</label>
                  <input type="checkbox" checked={detail.row.isActive === 'Yes'} disabled readOnly style={{ justifySelf: 'start' }} />
                </div>
                <table className="ref-formula-table" style={{ marginTop: 12 }}>
                  <thead>
                    <tr><th>Formula</th><th style={{ width: 140 }}>ExecutionOrder</th></tr>
                  </thead>
                  <tbody>
                    {lib.filter(f => detail.picked.has(f.formulaUqId))
                      .sort((a, b) => (detail.picked.get(a.formulaUqId) ?? 0) - (detail.picked.get(b.formulaUqId) ?? 0))
                      .map(f => (
                        <tr key={f.formulaUqId}>
                          <td>{f.formulaName}{f.kind === 1 && <span className="ref-script-tag">(Script)</span>}</td>
                          <td className="ref-order">{detail.picked.get(f.formulaUqId)}</td>
                        </tr>
                      ))}
                    {detail.picked.size === 0 && (
                      <tr><td colSpan={2} className="ref-combo-empty">No formulas assigned.</td></tr>
                    )}
                  </tbody>
                </table>
                {/* Row count, bottom-left of the grid (owner request 2026-10-12). */}
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ai-muted)' }}>
                  {detail.picked.size} formula{detail.picked.size === 1 ? '' : 's'}
                </div>
              </>
            ) : (
              <div className="ref-combo-empty">Select a mapping in List View to see its formulas.</div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Formulas — pick the exact Test+Matrix+Method, tick formulas + order. */}
      {editor && (
        <div className="ref-overlay" onClick={() => setEditor(null)}>
          <div className="ref-dialog" onClick={e => e.stopPropagation()} {...modalKeys}>
            <div className="ref-dialog-hdr">
              <span><i className="ti ti-math-function" /> {editor.viewOnly ? 'View Formulas' : editor.combo ? 'Edit Formulas' : 'Add Formulas'}</span>
              <i className="ti ti-x ref-dialog-x" onClick={() => setEditor(null)} />
            </div>

            <div className="ref-dialog-body">
              {!editor.combo ? (
                <>
                  <input autoFocus className="ref-combo-search" placeholder="Search Test / Matrix / Method…"
                    value={comboSearch} onChange={e => setComboSearch(e.target.value)} />
                  <div className="ref-combo-list">
                    {comboMatches.map(c => (
                      <div key={c.testMethodOid} className="ref-combo-row"
                        onClick={() => setEditor(ed => ed && ({ ...ed, combo: c }))}>
                        <b>{c.test}</b> · {c.matrix} · {c.method}
                      </div>
                    ))}
                    {comboMatches.length === 0 && <div className="ref-combo-empty">No matches.</div>}
                  </div>
                </>
              ) : (
                // The test-title header was replaced by a formula Search box (owner request
                // 2026-10-15); the test context is already in the dialog title bar / the row
                // that opened it. The "change" link stays for the Add flow (not-yet-mapped combo).
                <div className="ref-combo-current" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    className="ref-formula-search"
                    style={{ flex: 1, minWidth: 0, height: 30, fontSize: 13, padding: '0 10px', border: '1px solid var(--ai-line)', borderRadius: 4, boxSizing: 'border-box' }}
                    placeholder="Search formulas…"
                    value={editorSearch}
                    onChange={e => setEditorSearch(e.target.value)}
                  />
                  {!editor.viewOnly && !maps.some(m => m.testMethodOid === editor.combo!.testMethodOid) && (
                    <span className="ref-combo-change" onClick={() => setEditor(ed => ed && ({ ...ed, combo: null }))}>change</span>
                  )}
                </div>
              )}

              <table className="ref-formula-table">
                <thead>
                  <tr>
                    {!editor.viewOnly && <th style={{ width: 34 }} />}
                    <th>Formula</th>
                  </tr>
                </thead>
                <tbody>
                  {editorFormulas.length === 0 && (
                    <tr><td colSpan={editor.viewOnly ? 1 : 2} className="ref-combo-empty">
                      {editorSearch.trim() ? 'No formulas match your search.' : 'No formulas.'}
                    </td></tr>
                  )}
                  {editorFormulas.map(f => {
                    const on = editor.picked.has(f.formulaUqId);
                    return (
                      <tr key={f.formulaUqId}>
                        {!editor.viewOnly && (
                          <td className="ref-chk">
                            <input type="checkbox" checked={on} onChange={e => setEditor(ed => {
                              if (!ed) return ed;
                              const picked = new Map(ed.picked);
                              if (e.target.checked) picked.set(f.formulaUqId, f.series); else picked.delete(f.formulaUqId);
                              return { ...ed, picked };
                            })} />
                          </td>
                        )}
                        <td>{f.formulaName}
                          {f.kind === 1 && <span className="ref-script-tag">(Script)</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="ref-dialog-ftr">
              {/* Row count, bottom-left (owner request 2026-10-12): formulas shown in
                  the table — all library formulas when editing, the assigned ones when viewing. */}
              <span className="ref-ftr-count">
                {(() => { const n = editorFormulas.length; return `${n} formula${n === 1 ? '' : 's'}`; })()}
              </span>
              <div className="ref-ftr-btns">
                {editor.viewOnly ? (
                  <button className="ca-btn" onClick={() => setEditor(null)}>Close</button>
                ) : (
                  <>
                    <button className="ca-btn ca-btn-solid" onClick={() => void saveEditor()}>Save</button>
                    <button className="ca-btn" onClick={() => setEditor(null)}>Cancel</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copy Formula — legacy grid-lookup (old form, image 454): a GRID of every test
          method (Test · Matrix · Method · FormulaCount) with a search box + pagination;
          pick the SOURCE (click a row or tick it + OK) and its formulas copy onto the
          selected target. copyFrom warns if the chosen source has no formulas. */}
      {copyOpen && (
        <AppDialog
          open={copyOpen}
          title={`Copy Formula${selectedRow ? ` → ${selectedRow.test ?? ''}` : ''}`}
          width={820}
          className="dlg-center"
          maximizable
          onClose={() => setCopyOpen(false)}
          footer={
            <>
              <button type="button" className="ca-btn ca-btn-solid" disabled={copySel.size === 0}
                onClick={() => { const src = maps.find(m => copySel.has(m.testMethodOid)); if (src) void copyFrom(src); }}>
                OK
              </button>
              <button type="button" className="ca-btn" onClick={() => setCopyOpen(false)}>Cancel</button>
            </>
          }
        >
          <div style={{ marginBottom: 8 }}>
            <input autoFocus className="ref-combo-search" placeholder="Search a test method…"
              value={copySearch} onChange={e => setCopySearch(e.target.value)} />
          </div>
          <AppDataGrid<MapRow>
            rows={maps.filter(m => m.testMethodOid !== selectedRow?.testMethodOid
              && (copySearch.trim() === '' || [m.test, m.matrix, m.method].some(v => (v ?? '').toLowerCase().includes(copySearch.trim().toLowerCase()))))}
            columns={mapCols.slice(0, 4)}
            getRowId={r => r.testMethodOid}
            defaultPageSize={20}
            initialSort={{ key: 'test', dir: 'asc' }}
            selectable
            selectedIds={copySel}
            onSelectionChange={next => {
              // single-select: keep only the newly-ticked row (or clear on untick)
              const added = [...next].find(id => !copySel.has(id));
              setCopySel(added != null ? new Set([added]) : new Set());
            }}
            onRowClick={r => setCopySel(new Set([r.testMethodOid]))}
            emptyText="No test methods to copy from."
          />
        </AppDialog>
      )}

      {/* Remove Formula — tick which assigned formulas to remove from the test method
          (owner request — remove specific formulas, not all). */}
      {removeState && (
        <div className="ref-overlay" onClick={() => setRemoveState(null)}>
          <div className="ref-dialog" onClick={e => e.stopPropagation()}>
            <div className="ref-dialog-hdr">
              <span><i className="ti ti-trash" /> Remove Formula → <b>{removeState.row.test}</b></span>
              <i className="ti ti-x ref-dialog-x" onClick={() => setRemoveState(null)} />
            </div>
            <div className="ref-dialog-body">
              <div style={{ fontSize: 12, color: 'var(--ai-muted)', margin: '0 0 8px' }}>
                Tick the formula(s) to remove, then click Remove.
              </div>
              <table className="ref-formula-table">
                <thead><tr><th style={{ width: 34 }} /><th>Formula</th><th style={{ width: 90 }}>Order</th></tr></thead>
                <tbody>
                  {[...removeState.formulas].sort((a, b) => a.executionOrder - b.executionOrder).map(f => {
                    const on = removeState.toRemove.has(f.formulaUqId);
                    const name = f.formulaName ?? lib.find(l => l.formulaUqId === f.formulaUqId)?.formulaName ?? `#${f.formulaUqId}`;
                    return (
                      <tr key={f.formulaUqId}>
                        <td className="ref-chk">
                          <input type="checkbox" checked={on} onChange={e => setRemoveState(s => {
                            if (!s) return s;
                            const toRemove = new Set(s.toRemove);
                            if (e.target.checked) toRemove.add(f.formulaUqId); else toRemove.delete(f.formulaUqId);
                            return { ...s, toRemove };
                          })} />
                        </td>
                        <td>{name}</td>
                        <td className="ref-order">{f.executionOrder}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="ref-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void confirmRemove()}>
                🗑 Remove{removeState.toRemove.size ? ` (${removeState.toRemove.size})` : ''}
              </button>
              <button className="ca-btn" onClick={() => setRemoveState(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
